<template>
    <div>
        <seo-title :content="$t('technology.meta.title')" />
        <seo-description :content="$t('technology.meta.description')" />
        <seo-author content="Deveo ApS" />
        <seo-og-title :content="$t('technology.meta.title')" />
        <seo-og-description :content="$t('technology.meta.description')" />
        <seo-og-image content="/static/images/opengraph/fb_og.png" />
        <section class="bg-deveo-grey-gradient no-p-b" style="overflow:hidden;padding-top:15vh;margin-top:-105px;">
            <div class="tech-container">
                <tech-bubble icon="amazons3" size="large"></tech-bubble>
                <tech-bubble icon="babel" size="small"></tech-bubble>
                <tech-bubble icon="bootstrap" size="medium"></tech-bubble>
                <tech-bubble icon="css3" size="small"></tech-bubble>
                <tech-bubble icon="docker" size="large"></tech-bubble>
                <tech-bubble icon="git" size="medium"></tech-bubble>
                <tech-bubble icon="github" size="large"></tech-bubble>
                <tech-bubble icon="html5" size="small"></tech-bubble>
                <tech-bubble icon="javascript" size="medium"></tech-bubble>
                <tech-bubble icon="jquery" size="small"></tech-bubble>
                <tech-bubble icon="laravel" size="large"></tech-bubble>
                <tech-bubble icon="less" size="small"></tech-bubble>
                <tech-bubble icon="mysql" size="medium"></tech-bubble>
                <tech-bubble icon="nginx" size="medium"></tech-bubble>
                <tech-bubble icon="nodejs" size="large"></tech-bubble>
                <tech-bubble icon="phpstorm" size="small"></tech-bubble>
                <tech-bubble icon="redis" size="large"></tech-bubble>
                <tech-bubble icon="sass" size="small"></tech-bubble>
                <tech-bubble icon="react" size="small"></tech-bubble>
                <tech-bubble icon="ssh" size="medium"></tech-bubble>
                <tech-bubble icon="vue" size="large"></tech-bubble>
                <tech-bubble icon="webpack" size="medium"></tech-bubble>
                <tech-bubble icon="wordpress" size="large"></tech-bubble>
            </div>
            <img class="arch" src="/static/images/layout/arch.svg" alt="Arch"/>
        </section>
        <section class="bg-deveo-white tech-intro">
            <div align="center">
                <h2 align="center">
                    {{ $t('technology.mainHeader') }}
                </h2>
                <p class="fix-width" align="center">
                    {{ $t('technology.mainText') }}
                </p>
            </div>
        </section>
        <section class="bg-deveo-light">
            <div class="container" align="center">
                <div class="row">
                    <div class="col-lg-6 col-md-6 terminal-col align-self-center">
                        <tech-terminal></tech-terminal>
                    </div>
                    <div class="col-lg-1 col-md-1"></div>
                    <div class="col-lg-5 col-md-5">
                        <h4 align="left">
                            {{ $t('technology.coreFirst') }} <span class="main-color">Core</span><sup class="sup-small"> by Deveo</sup>
                            <br> {{ $t('technology.coreSecond') }}
                        </h4>
                        <div class="divider-md"></div>
                        <div class="row small-icons-row" align="left">
                            <div class="col-lg-6">
                                <i class="ion-ios-bolt"></i><br>
                                <b>{{ $t('technology.corePointOne') }}</b><br>
                                <p>{{ $t('technology.corePointOneText') }}</p>
                            </div>
                            <div class="col-lg-6">
                                <i class="ion-ios-pulse"></i><br>
                                <b>{{ $t('technology.corePointTwo') }}</b><br>
                                <p>{{ $t('technology.corePointTwoText') }}</p>
                            </div>
                            <div class="col-lg-6">
                                <i class="ion-ios-locked"></i><br>
                                <b>{{ $t('technology.corePointThree') }}</b><br>
                                <p>{{ $t('technology.corePointThreeText') }}</p>
                            </div>
                            <div class="col-lg-6">
                                <i class="ion-arrow-graph-up-right main-color"></i><br>
                                <b>{{ $t('technology.corePointFour') }}</b><br>
                                <p>{{ $t('technology.corePointFourText') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <section class="bg-deveo-white">
            <div class="container">
                <div class="row">
                    <div class="col-lg-12" align="center">
                        <h2 align="center">
                            {{ $t('technology.opensourceHeader') }}
                        </h2>
                        <p class="fix-width" align="center">
                            {{ $t('technology.opensourceTextFirst') }}<br>
                            {{ $t('technology.opensourceTextSecond') }} <b style="font-weight:600">{{ totalDownloads }}</b> {{ $t('technology.opensourceTextThird') }}
                        </p>
                        <div style="height:50px"></div>
                    </div>
                    <tech-package type="laravel" header="core" :downloads="packages.core.downloads" :stars="packages.core.stars">
                        <p slot="description">
                            Artistic API framework based on laravel and larapi.
                        </p>
                    </tech-package>
                    <tech-package type="laravel" header="distributed-generators" :downloads="packages.distributedGenerators.downloads" :stars="packages.distributedGenerators.stars">
                        <p slot="description">
                            Smart generators for distributed laravel. Spin up new bundles in the matter of seconds.
                        </p>
                    </tech-package>
                    <tech-package type="laravel" header="laravel-api-authenticator" :downloads="packages.laravelApiAuth.downloads" :stars="packages.laravelApiAuth.stars">
                        <p slot="description">
                            API authentication made simple.
                        </p>
                    </tech-package>
                    <tech-package type="laravel" header="laravel-core-time-tracker" :downloads="packages.laravelCoreTimeTracker.downloads" :stars="packages.laravelCoreTimeTracker.stars">
                        <p slot="description">
                            Advanced and highly precise time tracking of core users.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-animate" :downloads="packages.vueAnimate.downloads" :stars="packages.vueAnimate.stars">
                        <p slot="description">
                            An easy way to use beautiful animations on your site. Ideal for hero style landing pages.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-error-tracker" :downloads="packages.vueErrorTracker.downloads" :stars="packages.vueErrorTracker.stars">
                        <p slot="description">
                            Vue error tracker keeps track of vue-resource http related errors, and provides an easy solution for tracking errors.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-online" :downloads="packages.vueOnline.downloads" :stars="packages.vueOnline.stars">
                        <p slot="description">
                            An easy way to show if a users browser is online with support for custom notification drivers.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-seo" :downloads="packages.vueSeo.downloads" :stars="packages.vueSeo.stars">
                        <p slot="description">
                            Easy way to add SEO to any vue based site. Works without jQuery.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-toastr" :downloads="packages.vueToastr.downloads" :stars="packages.vueToastr.stars">
                        <p slot="description">
                            A easy-to-use toastr notification plugin developed without jQuery.
                        </p>
                    </tech-package>
                    <tech-package type="vue" header="vue-tinymce" :downloads="packages.vueTinymce.downloads" :stars="packages.vueTinymce.stars">
                        <p slot="description">
                            TinyMCE wrapper for vue.js without jQuery. Plug and play style with translation support.
                        </p>
                    </tech-package>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
    import proPoint from '../components/pro-point'
    import otherTech from '../components/other-tech'
    import techBubble from '../components/tech-bubble'
    import techTerminal from '../components/terminal'
    import techPackage from '../components/package'
    import * as packageService from '../../../infrastructure/services/packages/packageService'
    export default {
        name: 'Technologies',
        data () {
            return {
                packages: {
                    vueAnimate: { downloads: '', stars: '' },
                    vueSeo: { downloads: '', stars: '' },
                    vueOnline: { downloads: '', stars: '' },
                    vueToastr: { downloads: '', stars: '' },
                    vueErrorTracker: { downloads: '', stars: '' },
                    vueTinymce: { downloads: '', stars: '' },
                    distributedGenerators: { downloads: '', stars: '' },
                    core: { downloads: '', stars: '' },
                    laravelApiAuth: { downloads: '', stars: '' },
                    laravelCoreTimeTracker: { downloads: '', stars: '' }
                }
            }
        },
        computed: {
            totalDownloads: function () {
                let number = this.packages.vueAnimate.downloads +
                this.packages.vueSeo.downloads +
                this.packages.vueOnline.downloads +
                this.packages.vueToastr.downloads +
                this.packages.vueErrorTracker.downloads +
                this.packages.vueTinymce.downloads +
                this.packages.distributedGenerators.downloads +
                this.packages.core.downloads +
                this.packages.laravelApiAuth.downloads +
                this.packages.laravelCoreTimeTracker.downloads
                return number
            }
        },
        components: {
            proPoint,
            otherTech,
            techBubble,
            techTerminal,
            techPackage
        },
        created () {
            this.getDownloads()
        },
        methods: {
            getDownloads () {
                packageService.getDownloads('vue-animate', {}, (response) => {
                    this.packages.vueAnimate.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-animate', {}, (response) => {
                    this.packages.vueAnimate.stars = response.data.stargazers_count
                }, () => {})
                packageService.getDownloads('vue-seo', {}, (response) => {
                    this.packages.vueSeo.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-seo', {}, (response) => {
                    this.packages.vueSeo.stars = response.data.stargazers_count
                }, () => {})
                packageService.getDownloads('vue-online', {}, (response) => {
                    this.packages.vueOnline.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-online', {}, (response) => {
                    this.packages.vueOnline.stars = response.data.stargazers_count
                }, () => {})
                packageService.getDownloads('vue-toastr', {}, (response) => {
                    this.packages.vueToastr.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-toastr', {}, (response) => {
                    this.packages.vueToastr.stars = response.data.stargazers_count
                }, () => {})
                packageService.getDownloads('vue-error-tracker', {}, (response) => {
                    this.packages.vueErrorTracker.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-error-tracker', {}, (response) => {
                    this.packages.vueErrorTracker.stars = response.data.stargazers_count
                }, () => {})
                packageService.getDownloads('vue-tinymce', {}, (response) => {
                    this.packages.vueTinymce.downloads = response.data.downloads
                }, () => {})
                packageService.getStars('vue-tinymce', {}, (response) => {
                    this.packages.vueTinymce.stars = response.data.stargazers_count
                }, () => {})
                packageService.getPackagistDownloads('distributed-generators', {}, (response) => {
                    this.packages.distributedGenerators.downloads = response.data.package.downloads.total
                    this.packages.distributedGenerators.stars = response.data.package.github_stars
                }, () => {})
                packageService.getPackagistDownloads('core', {}, (response) => {
                    this.packages.core.downloads = response.data.package.downloads.total
                    this.packages.core.stars = response.data.package.github_stars
                }, () => {})
                packageService.getPackagistDownloads('laravel-api-authenticator', {}, (response) => {
                    this.packages.laravelApiAuth.downloads = response.data.package.downloads.total
                    this.packages.laravelApiAuth.stars = response.data.package.github_stars
                }, () => {})
                packageService.getPackagistDownloads('laravel-core-time-tracker', {}, (response) => {
                    this.packages.laravelCoreTimeTracker.downloads = response.data.package.downloads.total
                    this.packages.laravelCoreTimeTracker.stars = response.data.package.github_stars
                }, () => {})
            }
        }
    }
</script>
<style type="scss" scoped>
    p{
        font-weight: 300;
        font-family: 'Open Sans', sans-serif;
    }
    h2{
        font-weight: 400;
        font-size: 2rem;
        color: #000;
        font-family: 'Poppins', sans-serif;
    }
    h4 {
        font-weight: 400;
    }
    .small-icons-row p{
        font-size: 14px;
        font-weight: 300;
    }
    .small-icons-row i{
        font-size: 2rem;
        color: #3A7FFF;
    }
    .small-icons-row b{
        font-weight: 400;
        font-size: 18px;
    }
    i.ion-heart{
        color: red;
    }
    i.ion-ios-bolt{
        color: #ffc107;
    }
    i.ion-ios-pulse{
        color: #f44336;
    }
    i.ion-ios-locked{
        color: #607d8b;
    }
    sup.sup-small{
        font-size: 10px;
        top: -1.2em;
    }
</style>
<style type="scss">
    .arch{
        width: 100%;
    }
    @keyframes float {
        0% {

            transform: translatey(0px);
        }
        50% {

            transform: translatey(-10px);
        }
        100% {

            transform: translatey(0px);
        }
    }
    @keyframes test {
        0% {
            left: calc(50vw - 65px);
            top: 50%;
            opacity: 0;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 1;
        }
    }
    .tech-container{
        width: 1500px;
        // background: red;
        position: relative;
        height: 400px;
        z-index: 100;
    }
    .tech-bubble-wrapper{
        animation: test 2s ease-in-out, float 10s ease-in-out infinite;
        background: #fff;
        border-radius: 100px;
        text-align: center;
        line-height: 100px;
        position: absolute;
        box-shadow: 0 15px 35px rgba(0,0,0,.1),0 3px 10px rgba(0,0,0,.07);
        z-index: 100;
        transition: 2s linear all;
    }
    .tech-bubble-wrapper:nth-of-type(2n){
        animation: test 2s ease-in-out, float 14s ease-in-out infinite;
    }
    .tech-bubble-wrapper:nth-of-type(3n){
        animation: test 2s ease-in-out, float 18s ease-in-out infinite;
    }
    .tech-bubble-wrapper img{
        max-width: 100%;
        max-height: 100%;
    }
    .tech-bubble-wrapper.small img{ padding: 15px; }
    .tech-bubble-wrapper.medium img{ padding: 20px; }
    .tech-bubble-wrapper.large img{ padding: 30px; }
    .tech-bubble-wrapper.small{ width: 70px; height: 70px; line-height: 70px; }
    .tech-bubble-wrapper.medium{ width: 100px; height: 100px; line-height: 100px; }
    .tech-bubble-wrapper.large{ width: 130px; height: 130px; line-height: 130px; }

    .tech-bubble-wrapper.amazons3{ background-color: #febd69; }
    .tech-bubble-wrapper.amazons3 img{ padding: 15px; }
    .tech-bubble-wrapper.laravel{ background-color: #f4645f; }
    .tech-bubble-wrapper.vue{ background-color: #4fc08d; }
    .tech-bubble-wrapper.docker img{ padding: 20px; }
    .tech-bubble-wrapper.jquery{ background-color: #0769ad; }
    .tech-bubble-wrapper.nginx{ background-color: #009639; }
    .tech-bubble-wrapper.bootstrap{ background-color: #563d7c; }
    .tech-bubble-wrapper.bootstrap img{ padding: 25px; }
    .tech-bubble-wrapper.sass{ background-color: #CB6699; }
    .tech-bubble-wrapper.webpack{ background-color: rgb(29,100,181); }
    .tech-bubble-wrapper.webpack img{ padding: 15px; }

    .tech-bubble-wrapper:nth-child(1){ top: 30px; left: 30px; }
    .tech-bubble-wrapper:nth-child(2){ top: 170px; left: 180px; }
    .tech-bubble-wrapper:nth-child(3){ top: 210px; left: 20px; }
    .tech-bubble-wrapper:nth-child(4){ top: 300px; left: 160px; }
    .tech-bubble-wrapper:nth-child(5){ top: 30px; left: 1110px; }
    .tech-bubble-wrapper:nth-child(6){ top: 240px; left: 1000px; }
    .tech-bubble-wrapper:nth-child(7){ top: 230px; left: 280px; }
    .tech-bubble-wrapper:nth-child(8){ top: 140px; left: 1420px; }
    .tech-bubble-wrapper:nth-child(9){ top: 40px; left: 240px; }
    .tech-bubble-wrapper:nth-child(10){ top: 260px; left: 460px; }
    .tech-bubble-wrapper:nth-child(11){ top: 80px; left: 380px; }
    .tech-bubble-wrapper:nth-child(12){ top: 40px; left: 510px; }
    .tech-bubble-wrapper:nth-child(13){ top: 160px; left: 1260px; }
    .tech-bubble-wrapper:nth-child(14){ top: 260px; left: 1170px; }
    .tech-bubble-wrapper:nth-child(15){ top: 260px; left: 580px; }
    .tech-bubble-wrapper:nth-child(16){ top: 30px; left: 1400px; }
    .tech-bubble-wrapper:nth-child(17){ top: 60px; left: 610px; }
    .tech-bubble-wrapper:nth-child(18){ top: 200px; left: 720px; }
    .tech-bubble-wrapper:nth-child(19){ top: 320px; left: 750px; }
    .tech-bubble-wrapper:nth-child(20){ top: 60px; left: 780px; }
    .tech-bubble-wrapper:nth-child(21){ top: 200px; left: 830px; }
    .tech-bubble-wrapper:nth-child(22){ top: 280px; left: 1360px; }
    .tech-bubble-wrapper:nth-child(23){ top: 50px; left: 920px; }

    .tech-bubble-wrapper{ top: -200px; left: 50px; }

    .tech-container{
        width: 100%;
        height: 430px;
        overflow-x: auto;
        overflow-y: hidden;
    }
    .tech-container::-webkit-scrollbar {
        display: none;
    }
    .tech-intro{
        margin-top: -1px;
    }
    @media screen and (min-width:1500px){
        .tech-container{
            width: 1500px;
            margin: 0 auto;
        }
    }
</style>



// WEBPACK FOOTER //
// index.vue?d39336aa